import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getConsolidatedCantonsFilteredByRegions } from '../utils/cantons';
import api, { authAPI } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import { trackEvent } from '../utils/facebookPixel';
import { getAttributionForSubmission } from '../utils/attributionTracker';
import { formatDateDDMMYYYYSwiss } from '../utils/swissDateTime';
import RegistrationPartnerAndUpsells from '../components/RegistrationPartnerAndUpsells';
import ContinueTeamSetupStep, {
  buildContinuePartnerPlan,
} from '../components/ContinueTeamSetupStep';
import RegistrationPromoCodeField from '../components/RegistrationPromoCodeField';
import { useRegistrationPromoCode } from '../hooks/useRegistrationPromoCode';
import { playerAPI } from '../services/api';

const RegisterContainer = styled.div`
  max-width: ${(p) => (p.$wide ? '980px' : '800px')};
  margin: 2rem auto;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
`;

const RegisterHeader = styled.div`
  background: linear-gradient(135deg, var(--brand-red) 0%, var(--primary-blue) 50%, var(--primary-blue-dark) 100%);
  color: var(--white);
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(213, 43, 30, 0.3) 0%, transparent 50%, rgba(4, 54, 99, 0.2) 100%);
    pointer-events: none;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
`;

const FormContainer = styled.div`
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--primary-blue);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--light-grey);
  padding-bottom: 0.5rem;
`;

const WizardStepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  padding: 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const WizardStepperTrack = styled.div`
  position: absolute;
  top: 17px;
  left: ${(p) => `calc(100% / ${Math.max(2, p.$count || 3) * 2})`};
  right: ${(p) => `calc(100% / ${Math.max(2, p.$count || 3) * 2})`};
  height: 3px;
  background: #e5e7eb;
  border-radius: 999px;
  z-index: 0;
`;

const WizardStepperFill = styled.div`
  position: absolute;
  top: 17px;
  left: ${(p) => `calc(100% / ${Math.max(2, p.$count || 3) * 2})`};
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #043663, #0a558c);
  z-index: 0;
  width: ${(p) => {
    const n = Math.max(2, p.$count || 3);
    const pct = Math.min(100, Math.max(0, Number(p.$pct) || 0)) / 100;
    return `calc((100% - (100% / ${n})) * ${pct})`;
  }};
  max-width: ${(p) => `calc(100% - (100% / ${Math.max(2, p.$count || 3)}))`};
  transition: width 0.25s ease;
`;

const WizardStepItem = styled.button`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  min-width: 0;
  max-width: 100%;
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  font: inherit;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 4px;
    border-radius: 8px;
  }
`;

const WizardStepBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) => (p.$done || p.$active ? '#043663' : '#e5e7eb')};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#9ca3af')};
  box-shadow: ${(p) => (p.$active ? '0 4px 12px rgba(4, 54, 99, 0.35)' : 'none')};
`;

const WizardStepLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p) => (p.$active || p.$done ? '#043663' : '#9ca3af')};
  margin-top: 0.35rem;
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  padding: 0 0.15rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  overflow-wrap: anywhere;
`;

const WizardNavRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  margin-top: 1.25rem;
`;

const WizardNavButton = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#043663')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CompactTotalBar = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  align-items: center;
  justify-content: space-between;
`;

const CompactTotalLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
`;

const CompactTotalValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: #043663;
`;

const CheckoutSummary = styled.div`
  margin: 1rem 0 1.25rem;
  padding: 1.1rem 1.15rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
`;

const CheckoutLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9rem;
  color: #374151;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const CheckoutTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 2px solid #e2e8f0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
  span:last-child {
    color: #d52b1e;
  }
`;

const PlayerRegisterShell = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgb(30 64 175 / 0.1);
  }
  
  &.error {
    border-color: var(--error);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 1rem;
  background: var(--white);
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgb(30 64 175 / 0.1);
  }
  
  &.error {
    border-color: var(--error);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const HelpText = styled.small`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const ErrorBanner = styled.div`
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const ErrorIcon = styled.div`
  color: #d52b1e;
  font-size: 1.25rem;
  margin-top: 0.125rem;
`;

const ErrorContent = styled.div`
  flex: 1;
`;

const ErrorTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #d52b1e;
  margin: 0 0 0.25rem 0;
`;

const ErrorText = styled.p`
  color: #d52b1e;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1.5rem 0;
  
  &.optional {
    padding: 1rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    border-left: 4px solid #3b82f6;
  }
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  line-height: 1.5;
  cursor: pointer;
`;

const TermsLink = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: var(--primary-blue);
  text-decoration: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;

const PricingSection = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #043663;
  padding: 1.5rem;
  border-radius: 12px;
  margin: 1.5rem 0;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(4, 54, 99, 0.1);
`;

const PriceDisplay = styled.div`
  margin-bottom: 1rem;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: var(--text-muted);
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

const CurrentPrice = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #d52b1e;
`;

const DiscountInfo = styled.div`
  color: var(--success);
  font-weight: 600;
  margin-top: 0.5rem;
`;

const PriceDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #d52b1e 0%, #e6392a 100%);
  color: #ffffff;
  border: 2px solid #d52b1e;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  box-shadow: 0 4px 6px -1px rgba(213, 43, 30, 0.2);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #b8241a 0%, #d52b1e 100%);
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(213, 43, 30, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: #6b7280;
    border-color: #6b7280;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  color: var(--white);
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  font-size: 1.1rem;
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--brand-blue, #043663) 0%, var(--brand-blue-dark, #032a4a) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: white;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--brand-blue, #043663);
  margin: 0 0 0.5rem 0;
`;

const ModalText = styled.p`
  color: var(--text-secondary, #6b7280);
  line-height: 1.6;
  margin: 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  min-height: 44px; /* Ensure minimum touch target */
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, var(--brand-red, #d52b1e) 0%, var(--brand-red-dark, #b82318) 100%);
    color: white !important;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(213, 43, 30, 0.3);
      background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
    }
    
    &:focus {
      outline: 2px solid #d52b1e;
      outline-offset: 2px;
    }
  ` : `
    background: transparent;
    color: var(--text-secondary, #6b7280);
    border-color: var(--border-color, #d1d5db);
    
    &:hover {
      background: var(--bg-secondary, #f9fafb);
      color: var(--text-primary, #1f2937);
    }
  `}
`;

const ModalLink = styled(Link)`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px; /* Ensure minimum touch target */
  background: linear-gradient(135deg, var(--brand-blue, #043663) 0%, var(--brand-blue-dark, #032a4a) 100%);
  color: white !important;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);
    color: white !important;
    background: linear-gradient(135deg, #064e8b 0%, #043663 100%);
  }
  
  &:focus {
    outline: 2px solid #043663;
    outline-offset: 2px;
    color: white !important;
  }
`;



// Main registration component
const RegisterForm = () => {
  const { t } = useTranslation();
  const { register: registerUser, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [partnerUpsell, setPartnerUpsell] = useState({
    partner: null,
    upsellSelections: [],
    fulfillment: {
      method: 'delivery',
      useDifferentAddress: false,
      firstName: '',
      lastName: '',
      phone: '',
      street: '',
      postalCode: '',
      city: '',
      country: 'CH',
    },
  });
  
  // Get stored attribution data from session storage
  const [attributionData, setAttributionData] = useState(null);
  
  // Load attribution data on component mount and add client info for Meta Pixel
  useEffect(() => {
    const loadAttributionWithClientInfo = async () => {
      const storedAttribution = getAttributionForSubmission();
      
      // Add client user agent (always available)
      const clientUserAgent = navigator.userAgent;
      
      // Try to get client IP address (optional - may fail due to CORS)
      let clientIpAddress = null;
      try {
        // Use a public IP API (optional - for better Meta match quality)
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        if (ipResponse.ok) {
          const ipData = await ipResponse.json();
          clientIpAddress = ipData.ip;
        }
      } catch (error) {
        // IP fetch is optional, don't block registration
        console.log('Could not fetch client IP (optional):', error.message);
      }
      
      // Merge attribution data with client info
      const enrichedAttribution = {
        ...storedAttribution,
        client_user_agent: clientUserAgent,
        client_ip_address: clientIpAddress
      };
      
      console.log('🎯 Attribution data enriched:', {
        has_fbc: !!enrichedAttribution?.fbc,
        has_fbp: !!enrichedAttribution?.fbp,
        has_client_ip: !!clientIpAddress,
        has_user_agent: !!clientUserAgent
      });
      
      setAttributionData(enrichedAttribution);
    };
    
    loadAttributionWithClientInfo();
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlayerRegisterRoute = location.pathname.startsWith('/player/register-team');
  const isPlayerWizard =
    isPlayerRegisterRoute && (user?.accountType === 'player' || user?.type === 'player');
  const [wizardStep, setWizardStep] = useState(1);
  const [reusableTeams, setReusableTeams] = useState([]);
  const [hasContinueGate, setHasContinueGate] = useState(false);
  const [continueGateLoading, setContinueGateLoading] = useState(() => {
    if (!isPlayerWizard) return false;
    return !!(searchParams.get('tenant_id') && searchParams.get('campaign_id'));
  });
  const [continueSetup, setContinueSetup] = useState({
    decision: null,
    teamId: '',
    teamName: '',
    partnerMode: 'keep',
    removedIds: [],
  });
  const continueMode = continueSetup.decision === 'continue';
  const [loading, setLoading] = useState(false);
  
  // SEO Meta Tags
  useSEO({
    title: 'Register Your Team - Join Helvetic Padel Series',
    description: 'Register your padel team for Switzerland\'s premier league! Join 500+ teams competing across all cantons. Easy registration process with secure payment. Start your journey to the Swiss Finals!',
    keywords: 'padel registration, register padel team, join padel league, padel team signup, Switzerland padel, padel competition registration, helvetic padel series registration, padel league signup',
    canonical: 'https://helveticpadelseries.ch/register'
  });

  // Load reusable captain teams for continue-into-campaign gate
  useEffect(() => {
    if (!isPlayerWizard) {
      setContinueGateLoading(false);
      return;
    }
    const tenantId = searchParams.get('tenant_id');
    const campaignId = searchParams.get('campaign_id');
    if (!tenantId || !campaignId) {
      setContinueGateLoading(false);
      return;
    }
    let cancelled = false;
    setContinueGateLoading(true);
    (async () => {
      try {
        const res = await playerAPI.getCampaigns(tenantId);
        const list = res?.data || res || [];
        const campaigns = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : [];
        const campaign = campaigns.find((c) => String(c.id) === String(campaignId));
        const reuse = campaign?.reusableTeams || [];
        if (cancelled) return;
        setReusableTeams(reuse);
        if (reuse.length > 0) {
          setHasContinueGate(true);
          setWizardStep(0);
          setContinueSetup((prev) => ({
            ...prev,
            teamId: prev.teamId || reuse[0].teamId,
            teamName: prev.teamName || reuse[0].teamName || '',
            previousPrefill: reuse[0].teamName || '',
          }));
        } else {
          setHasContinueGate(false);
          setWizardStep(1);
        }
      } catch {
        if (!cancelled) {
          setHasContinueGate(false);
          setWizardStep(1);
        }
      } finally {
        if (!cancelled) setContinueGateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPlayerWizard, searchParams]);

  // Track registration page view
  useEffect(() => {
    trackEvent('ViewContent', {
      content_name: 'Registration Page',
      content_category: 'Registration'
    });
  }, []);

  const [registrationCantons, setRegistrationCantons] = useState(() =>
    getConsolidatedCantonsFilteredByRegions([])
  );
  const [currentPrice, setCurrentPrice] = useState(null);
  const [registrationCurrency, setRegistrationCurrency] = useState('CHF');
  const [discountInfo, setDiscountInfo] = useState('');
  const [currentTeamCount, setCurrentTeamCount] = useState(0);
  const [discountTier, setDiscountTier] = useState('regular');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorField, setErrorField] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [checkingTeamName, setCheckingTeamName] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [seasonStatus, setSeasonStatus] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(true);
  const [swissTenantName, setSwissTenantName] = useState('Switzerland');

  // Fetch season status for the league in the URL (court) — not the player's home/country tenant
  useEffect(() => {
    const fetchSeasonStatus = async () => {
      const urlTenantId = searchParams.get('tenant_id') || undefined;
      const urlSeasonId = searchParams.get('season_id') || undefined;
      try {
        setSeasonLoading(true);
        console.log('🔍 Fetching season status...', { urlTenantId, urlSeasonId });
        const response = await authAPI.getSeasonStatus(urlTenantId, urlSeasonId);
        console.log('🔍 Full API Response:', response);
        console.log('🔍 Response.success:', response.success);
        console.log('🔍 Response.data:', response.data);
        
        if (response.success) {
          console.log('🔍 Setting season status:', response.data);
          console.log('🔍 registrationOpen value:', response.data?.registrationOpen);
          console.log('🔍 registrationStartsIn value:', response.data?.registrationStartsIn);
          setSeasonStatus(response.data);
          const s = response.data?.season;
          if (s) {
            setRegistrationCantons(getConsolidatedCantonsFilteredByRegions(s.regionsAllowed));
            if (s.currency) setRegistrationCurrency(s.currency);
            const fee = s.registrationFee;
            if (fee != null && !Number.isNaN(Number(fee)) && Number(fee) >= 0) {
              setCurrentPrice(Number(fee));
              setDiscountInfo('Season registration fee');
            } else {
              setCurrentPrice(null);
            }
          } else {
            setRegistrationCantons(getConsolidatedCantonsFilteredByRegions([]));
            setCurrentPrice(null);
          }
        } else {
          console.warn('⚠️ API returned success: false', response);
          // Set default status if API fails
          setSeasonStatus({
            season: null,
            registrationOpen: true,
            message: 'Unable to check registration status'
          });
          setCurrentPrice(null);
        }
      } catch (error) {
        console.error('❌ Error fetching season status:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        // Set default status if API fails
        setSeasonStatus({
          season: null,
          registrationOpen: true,
          message: 'Unable to check registration status'
        });
        setCurrentPrice(null);
      } finally {
        setSeasonLoading(false);
      }
    };

    fetchSeasonStatus();
  }, [searchParams]);

  // Legacy public /register defaults to Switzerland; player campaign path uses URL tenant.
  useEffect(() => {
    let mounted = true;
    const urlTenantId = searchParams.get('tenant_id');
    if (isPlayerWizard && urlTenantId) {
      (async () => {
        try {
          const r = await authAPI.getTenantsForPlayerSignup();
          const tenants = Array.isArray(r?.data) ? r.data : (r?.data?.data || []);
          const match = tenants.find((t) => String(t.id) === String(urlTenantId));
          if (mounted && match?.name) setSwissTenantName(match.name);
        } catch {
          // keep previous label
        }
      })();
      return () => {
        mounted = false;
      };
    }
    const setSwissRegistrationTenant = async () => {
      try {
        const r = await authAPI.getTenantsForPlayerSignup();
        const tenants = Array.isArray(r?.data) ? r.data : (r?.data?.data || []);
        const swiss = tenants.find((t) => String(t.slug || '').toLowerCase() === 'switzerland');
        if (swiss?.id) {
          if (mounted) setSwissTenantName(swiss.name || 'Switzerland');
          try {
            sessionStorage.setItem('registrationXTenantId', swiss.id);
          } catch {
            // ignore storage errors
          }
        }
      } catch (e) {
        console.warn('Could not set Switzerland registration tenant:', e?.message || e);
      }
    };
    setSwissRegistrationTenant();
    return () => {
      mounted = false;
      try {
        sessionStorage.removeItem('registrationXTenantId');
      } catch {
        // ignore storage errors
      }
    };
  }, [isPlayerWizard, searchParams]);

  // Determine registration status
  const getRegistrationStatus = () => {
    console.log('🔍 getRegistrationStatus called');
    console.log('🔍 seasonStatus:', seasonStatus);
    console.log('🔍 seasonLoading:', seasonLoading);
    
    if (!seasonStatus || seasonLoading) {
      console.log('🔍 Status: loading (no status or still loading)');
      return 'loading';
    }

    // Server uses admin registration_start / registration_end instants; trust it before local heuristics.
    if (
      seasonStatus.registrationOpen === true ||
      seasonStatus.registrationOpen === 'true' ||
      seasonStatus.registrationOpen === 1
    ) {
      console.log('✅ Status: OPEN (from backend registrationOpen)');
      return 'open';
    }

    const startsInMin = Number(seasonStatus.registrationStartsInMinutes);
    if (!Number.isNaN(startsInMin) && startsInMin > 0) {
      console.log('⏳ Status: upcoming (starts in minutes:', startsInMin, ')');
      return 'upcoming';
    }
    
    const s = seasonStatus.season;
    const regStartRaw = s?.registrationStart ?? s?.registration_start;
    const regEndRaw = s?.registrationEnd ?? s?.registration_end;

    // If we have a season object, do a client-side date check as fallback
    if (s && regStartRaw && regEndRaw) {
      const now = new Date();
      
      // Parse dates - handle both ISO strings and date objects
      let registrationStart = new Date(regStartRaw);
      let registrationEnd = new Date(regEndRaw);
      
      // If dates are invalid, fall back to backend flag
      if (isNaN(registrationStart.getTime()) || isNaN(registrationEnd.getTime())) {
        console.warn('⚠️ Invalid date format in season data, falling back to backend flag');
      } else {
        console.log('🔍 Client-side date check:', {
          now: now.toISOString(),
          registrationStart: registrationStart.toISOString(),
          registrationEnd: registrationEnd.toISOString(),
          isAfterStart: now >= registrationStart,
          isBeforeEnd: now <= registrationEnd,
          backendRegistrationOpen: seasonStatus.registrationOpen
        });
        
        // Check if current date is within registration period
        if (now >= registrationStart && now <= registrationEnd) {
          console.log('✅ Client-side check: Registration should be OPEN');
          return 'open';
        }
        
        // Check if registration hasn't started yet
        if (now < registrationStart) {
          const daysUntilStart = Math.ceil((registrationStart - now) / (1000 * 60 * 60 * 24));
          console.log('⏳ Client-side check: Registration starts in', daysUntilStart, 'days');
          return 'upcoming';
        }
        
        // Registration has ended
        if (now > registrationEnd) {
          console.log('❌ Client-side check: Registration has ended');
          return 'closed';
        }
      }
    }
    
    console.log('🔍 Checking registrationOpen (fallback):', seasonStatus.registrationOpen);
    
    const startsIn = Number(seasonStatus.registrationStartsIn);
    if (!Number.isNaN(startsIn) && startsIn > 0) {
      console.log('⏳ Status: upcoming (starts in', startsIn, 'days)');
      return 'upcoming';
    }
    
    console.log('❌ Status: CLOSED');
    return 'closed';
  };

  const registrationStatus = getRegistrationStatus();
  console.log('🔍 Current registrationStatus:', registrationStatus);

  // Clear field-specific errors when user starts typing
  const clearFieldError = (fieldName) => {
    if (errorField === fieldName) {
      setErrorMessage('');
      setErrorField('');
    }
    // Clear individual field error
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Scroll to first error field
  const scrollToFirstError = (fieldName) => {
    setTimeout(() => {
      const element = document.getElementById(fieldName);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        element.focus();
      }
    }, 100);
  };

  // Show modal when component mounts and scroll to top
  useEffect(() => {
    // Modal disabled - commented out to turn off the popup
    // setShowModal(true);
    // Scroll to top of the page when component mounts
    window.scrollTo(0, 0);
  }, []);

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useForm();

  const watchedTeamName = watch('teamName');
  const watchedCanton = watch('canton');

  const playerCartTotals = (() => {
    const catalog = partnerUpsell.catalogByVariantId || {};
    const priceOf = (variantId) => {
      const meta = catalog[variantId];
      if (meta == null) return 0;
      return typeof meta === 'object' ? Number(meta.unitPriceChf) || 0 : Number(meta) || 0;
    };
    const labelOf = (variantId) => {
      const meta = catalog[variantId];
      if (meta && typeof meta === 'object' && meta.label) return meta.label;
      const byProduct = partnerUpsell.catalogByProductId || {};
      for (const p of Object.values(byProduct)) {
        const v = (p.variants || []).find((x) => x.id === variantId);
        if (v) {
          const parts = [p.name, v.sizeLabel || v.colorLabel || v.optionLabel].filter(Boolean);
          return parts.join(' · ') || p.name || 'Item';
        }
      }
      return 'Item';
    };
    const isMerch = (variantId) => {
      const meta = catalog[variantId];
      return !!(meta && typeof meta === 'object' && meta.includesMerchandise);
    };
    const lines = (partnerUpsell.upsellSelections || []).map((s) => {
      const qty = Math.max(1, s.quantity || 1);
      const unit = priceOf(s.variantId);
      return {
        variantId: s.variantId,
        label: labelOf(s.variantId),
        quantity: qty,
        lineTotal: Math.round(unit * qty * 100) / 100,
      };
    });
    const packageTotal = lines.reduce((sum, l) => sum + l.lineTotal, 0);
    const hasMerchandise = (partnerUpsell.upsellSelections || []).some((s) =>
      isMerch(s.variantId)
    );
    const deliveryFee =
      hasMerchandise ? Number(partnerUpsell.deliveryFeeChf) || 0 : 0;
    const payable = Math.round((packageTotal + deliveryFee) * 100) / 100;
    const hasPackage = (partnerUpsell.upsellSelections || []).some((s) => {
      const meta = catalog[s.variantId];
      return (
        meta &&
        typeof meta === 'object' &&
        (meta.productKind === 'entry_pass' || meta.productKind === 'pass_upgrade')
      );
    });
    return { lines, packageTotal, deliveryFee, payable, hasMerchandise, hasPackage };
  })();

  const getPromoValidatePayload = useCallback(
    () => ({
      seasonId: searchParams.get('season_id') || seasonStatus?.season?.id || null,
      campaignId: searchParams.get('campaign_id') || null,
      tenant_id: searchParams.get('tenant_id') || null,
      teamId:
        continueSetup.decision === 'continue' && continueSetup.teamId
          ? continueSetup.teamId
          : null,
      cartTotal: playerCartTotals.payable,
    }),
    [
      searchParams,
      seasonStatus?.season?.id,
      continueSetup.decision,
      continueSetup.teamId,
      playerCartTotals.payable,
    ]
  );

  const {
    promoCodeInput,
    onPromoInputChange,
    promoCodeValid,
    promoCodeMessage,
    promoCodeData,
    promoApplying,
    applyPromoCode,
    appliedPromoCode,
  } = useRegistrationPromoCode(getPromoValidatePayload);

  // Handle URL parameters for pre-filling promo code (user still clicks Apply)
  useEffect(() => {
    const promoCodeFromUrl = searchParams.get('promo_code');
    if (promoCodeFromUrl) {
      onPromoInputChange(promoCodeFromUrl);
    }
  }, [searchParams, onPromoInputChange]);

  /** Promo % / fixed applied to grand total (pack + shipping), not season League Pass fee. */
  const checkoutPromoDiscount = (() => {
    if (!promoCodeValid || !promoCodeData) return 0;
    const total = playerCartTotals.payable;
    const v = Number(promoCodeData.discountValue);
    if (Number.isNaN(v) || v < 0) return 0;
    if (promoCodeData.discountType === 'percentage') {
      return Math.round(total * (Math.min(v, 100) / 100) * 100) / 100;
    }
    return Math.round(Math.min(v, total) * 100) / 100;
  })();

  const goWizardNext = async () => {
    if (wizardStep === 0) {
      if (!continueSetup.decision) {
        setErrorMessage(
          t(
            'register.continueTeam.chooseRequired',
            'Please choose whether to move a previous team or create a new one.'
          )
        );
        return;
      }
      if (continueSetup.decision === 'continue') {
        if (!continueSetup.teamId) {
          setErrorMessage(t('register.continueTeam.pickTeamRequired', 'Select a team to continue.'));
          return;
        }
        const name = (continueSetup.teamName || '').trim();
        if (name.length < 3) {
          setErrorMessage(t('register.teamNameMinLength', 'Team name must be at least 3 characters'));
          return;
        }
        const tenantId = searchParams.get('tenant_id');
        setCheckingTeamName(true);
        try {
          const res = await playerAPI.checkTeamName({
            teamName: name,
            tenantId,
            excludeTeamId: continueSetup.teamId,
          });
          const data = res?.data || res;
          if (data?.available === false) {
            setErrorMessage(
              data.message || t('register.teamNameTaken', 'That team name already exists in this league.')
            );
            return;
          }
        } catch (err) {
          setErrorMessage(
            err?.response?.data?.message ||
              t('register.teamNameCheckFailed', 'Could not check team name. Please try again.')
          );
          return;
        } finally {
          setCheckingTeamName(false);
        }
        setErrorMessage('');
        setWizardStep(2);
        return;
      }
      setErrorMessage('');
      setWizardStep(1);
      return;
    }
    if (wizardStep === 1) {
      const ok = await trigger(['teamName', 'canton']);
      if (!ok) return;
      const name = String(getValues('teamName') || '').trim();
      const tenantId = searchParams.get('tenant_id');
      setCheckingTeamName(true);
      try {
        const res = await playerAPI.checkTeamName({ teamName: name, tenantId });
        const data = res?.data || res;
        if (data?.available === false) {
          const msg =
            data.message || t('register.teamNameTaken', 'That team name already exists in this league.');
          setErrorMessage(msg);
          setErrorField('teamName');
          setFieldErrors({ teamName: msg });
          return;
        }
      } catch (err) {
        setErrorMessage(
          err?.response?.data?.message ||
            t('register.teamNameCheckFailed', 'Could not check team name. Please try again.')
        );
        return;
      } finally {
        setCheckingTeamName(false);
      }
      setErrorMessage('');
      setWizardStep(2);
      return;
    }
    if (wizardStep === 2) {
      if (!playerCartTotals.hasPackage) {
        setErrorMessage(
          t(
            'register.selectPackageRequired',
            'Please select a League Pass or package before checkout.'
          )
        );
        return;
      }
      setErrorMessage('');
      setWizardStep(3);
    }
  };

  const goWizardBack = () => {
    if (wizardStep === 2 && continueMode) {
      setWizardStep(0);
      return;
    }
    if (wizardStep === 1 && hasContinueGate) {
      setWizardStep(0);
      return;
    }
    setWizardStep((s) => Math.max(hasContinueGate ? 0 : 1, s - 1));
  };
  const goToWizardStep = (step) => {
    if (step < (hasContinueGate ? 0 : 1) || step >= wizardStep) return;
    if (continueMode && step === 1) return;
    setWizardStep(step);
  };

  const onSubmit = async (data) => {
    if (isPlayerWizard && wizardStep !== 3) {
      await goWizardNext();
      return;
    }
    // Check if registration is currently open before allowing submission
    if (registrationStatus === 'closed' || registrationStatus === 'upcoming') {
      setErrorMessage('Registration is currently closed. Please check the registration dates above.');
      return;
    }
    
    setLoading(true);
    setErrorMessage(''); // Clear previous errors
    setErrorField('');
    setFieldErrors({}); // Clear field-specific errors

    // Player portal: continue existing team OR create-team checkout
    if (isPlayerRegisterRoute && (user?.accountType === 'player' || user?.type === 'player')) {
      try {
        const seasonId = searchParams.get('season_id') || data.seasonId;
        const tenantId = searchParams.get('tenant_id');
        const campaignId = searchParams.get('campaign_id');
        const partner = partnerUpsell.partner;
        const catalog = partnerUpsell.catalogByVariantId || {};
        const hasMerchandise = (partnerUpsell.upsellSelections || []).some((s) => {
          const meta = catalog[s.variantId];
          return (
            meta &&
            typeof meta === 'object' &&
            (meta.includesMerchandise || meta.productKind === 'pass_upgrade')
          );
        });
        if (hasMerchandise && partnerUpsell.fulfillmentReady === false) {
          setErrorMessage(
            partnerUpsell.fulfillmentBlockReason ||
              t(
                'registrationUpsell.fixBeforeCheckout',
                'Please fix the delivery details above — then you can checkout.'
              )
          );
          setLoading(false);
          return;
        }
        const fulfillment = hasMerchandise
          ? {
              ...partnerUpsell.fulfillment,
              useDifferentAddress: !!partnerUpsell.fulfillment?.useDifferentAddress,
            }
          : null;

        if (continueMode) {
          if (!campaignId || !continueSetup.teamId) {
            setErrorMessage(
              t(
                'register.continueTeam.missingContext',
                'Missing campaign or team. Go back and try again.'
              )
            );
            setLoading(false);
            return;
          }
          trackEvent('InitiateCheckout', {
            content_name: 'Continue Team Campaign Checkout',
            content_category: 'Registration',
            value: 1,
            currency: 'CHF',
          });
          const freshAttribution = getAttributionForSubmission();
          const attributionPayload = {
            ...(attributionData || {}),
            ...(freshAttribution || {}),
            client_user_agent: navigator.userAgent,
            client_ip_address:
              attributionData?.client_ip_address || freshAttribution?.client_ip_address || null,
          };
          const res = await playerAPI.registerCampaignTeam(
            campaignId,
            continueSetup.teamId,
            tenantId,
            {
              season_id: seasonId || undefined,
              partner_plan: buildContinuePartnerPlan(continueSetup),
              team_name: (continueSetup.teamName || '').trim() || undefined,
              upsellSelections: partnerUpsell.upsellSelections || [],
              fulfillment,
              partner: partner
                ? partner.playerId
                  ? { playerId: partner.playerId }
                  : { email: partner.email }
                : null,
              promoCode: appliedPromoCode,
              attribution: attributionPayload,
            }
          );
          const checkoutUrl =
            res?.data?.checkoutUrl ||
            res?.data?.data?.checkoutUrl ||
            res?.checkoutUrl;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
          if (res?.alreadyRegistered || res?.data?.alreadyRegistered) {
            navigate(
              `/player/tournaments?tenant_id=${encodeURIComponent(tenantId)}&campaign_id=${encodeURIComponent(
                campaignId
              )}&season_id=${encodeURIComponent(seasonId || '')}&team_id=${encodeURIComponent(
                continueSetup.teamId
              )}&view=overview`
            );
            return;
          }
          setErrorMessage('Could not start checkout. Please try again.');
        } else {
          trackEvent('InitiateCheckout', {
            content_name: 'Player Team Registration Checkout',
            content_category: 'Registration',
            value: 1,
            currency: 'CHF',
          });
          const freshAttribution = getAttributionForSubmission();
          const attributionPayload = {
            ...(attributionData || {}),
            ...(freshAttribution || {}),
            client_user_agent: navigator.userAgent,
            client_ip_address:
              attributionData?.client_ip_address || freshAttribution?.client_ip_address || null,
          };
          const res = await playerAPI.startRegistrationCheckout({
            seasonId,
            teamName: data.teamName,
            email: data.email || user.email,
            canton: data.canton,
            promoCode: appliedPromoCode,
            campaignId: searchParams.get('campaign_id') || null,
            upsellSelections: partnerUpsell.upsellSelections || [],
            fulfillment,
            partner: partner
              ? partner.playerId
                ? { playerId: partner.playerId }
                : { email: partner.email }
              : null,
            attribution: attributionPayload,
          });
          const checkoutUrl = res?.data?.checkoutUrl || res?.checkoutUrl;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
          setErrorMessage('Could not start checkout. Please try again.');
        }
      } catch (error) {
        const data = error.response?.data;
        const hint = data?.hint;
        const mainMsg =
          data?.message ||
          error.message ||
          t(
            'registrationUpsell.checkoutFailed',
            'Checkout failed. Please fix any missing details and try again.'
          );
        setErrorMessage(hint ? `${mainMsg} ${hint}` : mainMsg);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    
    try {
      // Remove confirmPassword and required checkboxes from data sent to backend
      // Keep partnerCommunications as it's optional but should be passed to backend
      const { confirmPassword, agreeTerms, agreeRules, partnerCommunications, phoneNumber, password, ...registrationData } = data;
      
      // Register the team first
      // Add attribution data to registration
      const registrationDataWithAttribution = {
        ...registrationData,
        attribution: attributionData
      };
      
      console.log('🎯 Submitting registration with attribution:', registrationDataWithAttribution);
      
      const result = await registerUser(registrationDataWithAttribution);
      
      if (result.success) {
        // Track registration form submission (not completion - that happens after payment)
        trackEvent('InitiateCheckout', {
          content_name: 'Team Registration Form',
          content_category: 'Registration',
          value: 1,
          currency: 'CHF',
          custom_data: {
            team_name: data.teamName,
            canton: data.canton,
            registration_stage: 'form_submitted'
          }
        });
        
        // If payment is required, create Stripe checkout directly
        if (result.data.paymentRequired) {
          await createStripeCheckoutWithData(result.data);
        } else {
          navigate(isPlayerRegisterRoute ? '/player/teams' : '/dashboard');
        }
      } else {
        // Handle registration failure from AuthContext
        console.log('Registration failed:', result);
        
        // Handle multiple errors if present
        if (result.errors && Array.isArray(result.errors)) {
          const fieldErrorMap = {};
          result.errors.forEach(error => {
            fieldErrorMap[error.field] = error.message;
          });
          setFieldErrors(fieldErrorMap);
          setErrorMessage('Please fix the errors below');
          setErrorField(result.field || '');
          
          // Scroll to first error field
          if (result.errors.length > 0) {
            scrollToFirstError(result.errors[0].field);
          }
        } else {
          // Single error
          setErrorMessage(result.message || 'Registration failed. Please try again.');
          setErrorField(result.field || '');
          
          // Scroll to error field
          if (result.field) {
            scrollToFirstError(result.field);
          }
        }
        return;
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Show user-friendly error message
      let message = 'Registration failed. Please try again.';
      let field = '';
      
      // Check if it's an axios error response
      if (error.response?.data?.message) {
        message = error.response.data.message;
        field = error.response.data.field || '';
        
        // Handle multiple errors
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const fieldErrorMap = {};
          error.response.data.errors.forEach(err => {
            fieldErrorMap[err.field] = err.message;
          });
          setFieldErrors(fieldErrorMap);
          
          // Scroll to first error field
          if (error.response.data.errors.length > 0) {
            scrollToFirstError(error.response.data.errors[0].field);
          }
        }
      } 
      // Check if it's a direct error object (from AuthContext)
      else if (error.message) {
        message = error.message;
        field = error.field || '';
      }
      // Check if it's a result object with success: false
      else if (error.success === false && error.message) {
        message = error.message;
        field = error.field || '';
      }
      
      console.log('Setting error message:', message, 'field:', field);
      setErrorMessage(message);
      setErrorField(field);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const createStripeCheckout = async (teamId, registrationId) => {
    try {
      console.log('Creating checkout session with:', {
        registration_id: registrationId,
        team_id: teamId,
        success_url: `${window.location.origin}/payment-success?team_id=${teamId}`,
        cancel_url: `${window.location.origin}/payment-cancelled?team_id=${teamId}`
      });

      // Create Stripe Checkout Session directly
      const response = await api.post('/payments/create-checkout-session', {
        registration_id: registrationId,
        team_id: teamId,
        success_url: `${window.location.origin}/payment-success?team_id=${teamId}`,
        cancel_url: `${window.location.origin}/payment-cancelled?team_id=${teamId}`
      });

      console.log('Full API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);

      if (response.data && response.data.success) {
        console.log('Redirecting to:', response.data.data.checkout_url);
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.checkout_url;
      } else if (response.data && response.data.checkout_url) {
        // Fallback: if response doesn't have success wrapper
        console.log('Redirecting to (fallback):', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('API returned error:', response.data);
        setErrorMessage(`Payment setup failed: ${response.data?.message || 'Unknown error'}`);
      }

    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage(`Payment failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const createStripeCheckoutWithData = async (registrationData) => {
    try {
      console.log('Creating checkout session with data:', registrationData);

      // Create Stripe Checkout Session with team data
      const paymentOrigin = isPlayerRegisterRoute ? `${window.location.origin}/player` : `${window.location.origin}`;
      const successUrl = `${paymentOrigin}/payment-success?session_id={CHECKOUT_SESSION_ID}&team_id=${registrationData.team.id}`;
      const cancelUrl = `${paymentOrigin}/payment-cancelled?team_id=${registrationData.team.id}`;
      
      console.log('Success URL:', successUrl);
      console.log('Cancel URL:', cancelUrl);
      
      // Include phoneNumber and members in team_data for checkout
      // Note: Logo is removed from Stripe metadata due to size limits - teams can upload logo after registration
      const teamDataWithPhone = {
        ...registrationData.team,
        phoneNumber: registrationData.team?.phoneNumber || null,
        members: registrationData.team?.members || []
      };

      const response = await api.post('/public-payments/create-checkout-session', {
        team_data: teamDataWithPhone,
        season_data: registrationData.season,
        pricing_data: registrationData.pricing,
        success_url: successUrl,
        cancel_url: cancelUrl,
        attribution_data: attributionData
      });

      console.log('Full API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);

      if (response.data && response.data.success) {
        console.log('Opening checkout:', response.data.data.checkout_url);
        
        // Track checkout initiation
        trackEvent('InitiateCheckout', {
          content_name: 'Team Registration Checkout',
          content_category: 'Payment',
          value: registrationData.pricing?.finalPrice ?? registrationData.pricing?.basePrice ?? 0,
          currency: registrationData.pricing?.currency || registrationCurrency || 'CHF',
          custom_data: {
            team_name: registrationData.team.teamName,
            canton: registrationData.team.canton,
            registration_stage: 'checkout_started'
          }
        });
        
        // Simple direct redirect - works on all devices
        window.location.href = response.data.data.checkout_url;
        
      } else if (response.data && response.data.checkout_url) {
        // Fallback: if response doesn't have success wrapper
        console.log('Opening checkout (fallback):', response.data.checkout_url);
        
        // Track checkout initiation (fallback)
        trackEvent('InitiateCheckout', {
          content_name: 'Team Registration Checkout',
          content_category: 'Payment',
          value: registrationData.pricing?.finalPrice ?? registrationData.pricing?.basePrice ?? 0,
          currency: registrationData.pricing?.currency || registrationCurrency || 'CHF',
          custom_data: {
            team_name: registrationData.team.teamName,
            canton: registrationData.team.canton,
            registration_stage: 'checkout_started_fallback'
          }
        });
        
        window.location.href = response.data.checkout_url;
      } else {
        console.error('API returned error:', response.data);
        setErrorMessage(`Payment setup failed: ${response.data?.message || 'Unknown error'}`);
      }

    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage(`Payment failed: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <RegisterContainer $wide={isPlayerWizard}>
      <RegisterHeader>
        <Title>Join Helvetic Padel Series</Title>
        <Subtitle>
          {seasonStatus?.season?.name
            ? `${seasonStatus.season.name} - ${swissTenantName}`
            : `Team registration - ${swissTenantName}`}
        </Subtitle>
      </RegisterHeader>

      {/* Registration Status Banner */}
      {seasonStatus && !seasonLoading && (
        <RegistrationStatusBanner status={registrationStatus}>
          {registrationStatus === 'closed' || registrationStatus === 'upcoming' ? (
            <>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                {seasonStatus?.message || 'Registration is currently closed'}
              </div>
              {seasonStatus?.season && (
                <div style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {seasonStatus.season.name} • {formatDateDDMMYYYYSwiss(seasonStatus.season.startDate)} - {formatDateDDMMYYYYSwiss(seasonStatus.season.endDate)}
                  {seasonStatus?.registrationPage?.bannerExtraLine ? (
                    <div style={{ marginTop: '0.35rem', fontWeight: 500 }}>{seasonStatus.registrationPage.bannerExtraLine}</div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <>
              {seasonStatus.message}
              {seasonStatus.season && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                  {seasonStatus.season.name} • {formatDateDDMMYYYYSwiss(seasonStatus.season.startDate)} - {formatDateDDMMYYYYSwiss(seasonStatus.season.endDate)}
                  {seasonStatus?.registrationPage?.bannerExtraLine ? (
                    <div style={{ marginTop: '0.35rem', opacity: 0.95 }}>{seasonStatus.registrationPage.bannerExtraLine}</div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </RegistrationStatusBanner>
      )}

      <FormContainer>
        {registrationStatus === 'closed' || registrationStatus === 'upcoming' ? (
          <DisabledForm>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h3 style={{ marginBottom: '1rem', color: '#043663' }}>Registration Currently Closed</h3>
              <div style={{ fontSize: '1rem', lineHeight: '1.8', color: '#666' }}>
                <p style={{ marginBottom: '1rem', fontWeight: '600' }}>
                  {seasonStatus?.message || 'Registration is currently closed. Please check back later.'}
                </p>
                {seasonStatus?.season && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    {seasonStatus.season.name} • {formatDateDDMMYYYYSwiss(seasonStatus.season.startDate)} - {formatDateDDMMYYYYSwiss(seasonStatus.season.endDate)}
                    {seasonStatus?.registrationPage?.bannerExtraLine ? (
                      <>
                        <br />
                        <span style={{ fontWeight: 600 }}>{seasonStatus.registrationPage.bannerExtraLine}</span>
                      </>
                    ) : null}
                  </p>
                )}
              </div>
            </div>
          </DisabledForm>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              if (
                isPlayerWizard &&
                wizardStep < 3 &&
                e.key === 'Enter' &&
                e.target?.tagName !== 'TEXTAREA'
              ) {
                e.preventDefault();
              }
            }}
          >
          {errorMessage && (
            <ErrorBanner>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorContent>
                <ErrorTitle>{t('register.registrationError')}</ErrorTitle>
                <ErrorText>{errorMessage}</ErrorText>
              </ErrorContent>
            </ErrorBanner>
          )}

          {isPlayerWizard ? (
            <PlayerRegisterShell>
              {continueGateLoading ? (
                <FormSection style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                  <LoadingSpinner size="lg" />
                  <p
                    style={{
                      margin: '1.25rem 0 0',
                      color: '#64748b',
                      fontSize: '0.95rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {t(
                      'register.continueTeam.checkingTeams',
                      'Checking if you have a team from a previous season…'
                    )}
                  </p>
                </FormSection>
              ) : (
                <>
              <WizardStepperRow role="navigation" aria-label="Registration steps">
                {(() => {
                  const steps = continueMode
                    ? [
                        { step: 0, label: t('register.wizardStepTeam', 'Your team') },
                        { step: 2, label: t('register.wizardStepPackages', 'Packages') },
                        { step: 3, label: t('register.wizardStepCheckout', 'Checkout') },
                      ]
                    : hasContinueGate
                      ? [
                          { step: 0, label: t('register.wizardStepTeam', 'Your team') },
                          { step: 1, label: t('register.wizardStepDetails', 'Details') },
                          { step: 2, label: t('register.wizardStepPackages', 'Packages') },
                          { step: 3, label: t('register.wizardStepCheckout', 'Checkout') },
                        ]
                      : [
                          { step: 1, label: t('register.wizardStepDetails', 'Details') },
                          { step: 2, label: t('register.wizardStepPackages', 'Packages') },
                          { step: 3, label: t('register.wizardStepCheckout', 'Checkout') },
                        ];
                  const count = steps.length;
                  const activeIdx = Math.max(
                    0,
                    steps.findIndex((s) => s.step === wizardStep)
                  );
                  const fillPct =
                    count <= 1 ? 0 : (Math.max(0, activeIdx) / (count - 1)) * 100;
                  return (
                    <>
                      <WizardStepperTrack $count={count} aria-hidden />
                      <WizardStepperFill $count={count} $pct={fillPct} aria-hidden />
                      {steps.map(({ step, label }, i) => {
                        const done = wizardStep > step;
                        const active = wizardStep === step;
                        const clickable = done;
                        return (
                          <WizardStepItem
                            key={`${step}-${label}`}
                            type="button"
                            $clickable={clickable}
                            onClick={() => clickable && goToWizardStep(step)}
                            aria-current={active ? 'step' : undefined}
                          >
                            <WizardStepBubble $active={active} $done={done}>
                              {done ? '✓' : i + 1}
                            </WizardStepBubble>
                            <WizardStepLabel $active={active} $done={done}>
                              {label}
                            </WizardStepLabel>
                          </WizardStepItem>
                        );
                      })}
                    </>
                  );
                })()}
              </WizardStepperRow>

              {hasContinueGate && wizardStep === 0 ? (
                <FormSection>
                  <ContinueTeamSetupStep
                    reusableTeams={reusableTeams}
                    tenantId={searchParams.get('tenant_id')}
                    seasonId={searchParams.get('season_id')}
                    value={continueSetup}
                    onChange={setContinueSetup}
                    partnerUpsell={partnerUpsell}
                    onPartnerUpsellChange={setPartnerUpsell}
                  />
                  <WizardNavRow style={{ justifyContent: 'flex-end' }}>
                    <WizardNavButton
                      type="button"
                      $primary
                      onClick={goWizardNext}
                      disabled={checkingTeamName}
                    >
                      {checkingTeamName
                        ? t('register.checkingTeamName', 'Checking team name…')
                        : t('register.nextContinue', 'Next')}
                    </WizardNavButton>
                  </WizardNavRow>
                </FormSection>
              ) : null}

                <FormSection
                  style={{
                    display: wizardStep === 1 && !continueMode ? undefined : 'none',
                  }}
                >
                  <SectionTitle>{t('register.teamInformation')}</SectionTitle>
                  <FormGroup>
                    <Label htmlFor="teamName">{t('register.teamName')} *</Label>
                    <Input
                      id="teamName"
                      type="text"
                      {...register('teamName', {
                        required: !continueMode ? t('register.teamNameRequired') : false,
                        minLength: { value: 3, message: t('register.teamNameMinLength') },
                      })}
                      className={
                        errors.teamName || errorField === 'teamName' || fieldErrors.teamName
                          ? 'error'
                          : ''
                      }
                      onChange={(e) => {
                        clearFieldError('teamName');
                        register('teamName').onChange(e);
                      }}
                    />
                    {errors.teamName && <ErrorMessage>{errors.teamName.message}</ErrorMessage>}
                    {fieldErrors.teamName && <ErrorMessage>{fieldErrors.teamName}</ErrorMessage>}
                    <HelpText>{t('register.teamNameHelp')}</HelpText>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="canton">{t('register.canton')} *</Label>
                    <Select
                      id="canton"
                      {...register('canton', {
                        required: !continueMode ? t('register.cantonRequired') : false,
                      })}
                      className={errors.canton ? 'error' : ''}
                    >
                      <option value="">{t('register.selectCanton')}</option>
                      {(registrationCantons.length > 0
                        ? registrationCantons
                        : getConsolidatedCantonsFilteredByRegions([])
                      ).map((canton) => (
                        <option key={canton.code} value={canton.code}>
                          {canton.name}
                        </option>
                      ))}
                    </Select>
                    {errors.canton && <ErrorMessage>{errors.canton.message}</ErrorMessage>}
                  </FormGroup>
                </FormSection>

              <RegistrationPartnerAndUpsells
                tenantId={searchParams.get('tenant_id')}
                seasonId={searchParams.get('season_id')}
                value={partnerUpsell}
                onChange={setPartnerUpsell}
                sections={
                  wizardStep === 1 && !continueMode
                    ? 'partner'
                    : wizardStep === 2
                      ? 'packages'
                      : wizardStep === 3
                        ? 'fulfillment'
                        : 'none'
                }
              />

              {wizardStep === 2 && (
                <>
                  <CompactTotalBar>
                    <CompactTotalLabel>
                      {t('register.currentSelection', 'Current selection')}
                      {continueMode && continueSetup.teamName
                        ? ` · ${continueSetup.teamName}`
                        : watchedTeamName
                          ? ` · ${watchedTeamName}`
                          : ''}
                      {!continueMode && watchedCanton ? ` · ${watchedCanton}` : ''}
                    </CompactTotalLabel>
                    <CompactTotalValue>
                      {(registrationCurrency || 'CHF')}{' '}
                      {playerCartTotals.packageTotal.toFixed(2)}
                    </CompactTotalValue>
                  </CompactTotalBar>
                  <WizardNavRow>
                    <WizardNavButton type="button" onClick={goWizardBack}>
                      {continueMode
                        ? t('register.backToTeam', 'Back to team')
                        : t('register.backToDetails', 'Back to Details')}
                    </WizardNavButton>
                    <WizardNavButton
                      type="button"
                      $primary
                      onClick={goWizardNext}
                      disabled={!playerCartTotals.hasPackage}
                    >
                      {t('register.nextCheckout', 'Next: Checkout')}
                    </WizardNavButton>
                  </WizardNavRow>
                </>
              )}

              {wizardStep === 1 && (
                <WizardNavRow
                  style={{
                    justifyContent: hasContinueGate ? 'space-between' : 'flex-end',
                  }}
                >
                  {hasContinueGate ? (
                    <WizardNavButton type="button" onClick={goWizardBack}>
                      {t('register.backToTeam', 'Back to team')}
                    </WizardNavButton>
                  ) : null}
                  <WizardNavButton type="button" $primary onClick={goWizardNext} disabled={checkingTeamName}>
                    {checkingTeamName
                      ? t('register.checkingTeamName', 'Checking team name…')
                      : t('register.nextPackage', 'Next: Select Package')}
                  </WizardNavButton>
                </WizardNavRow>
              )}

              {wizardStep === 3 && (
                <FormSection>
                  <SectionTitle>{t('register.promotionalCode')}</SectionTitle>
                  <FormGroup>
                    <Label htmlFor="promoCode">{t('register.promotionalCode')}</Label>
                    <RegistrationPromoCodeField
                      id="promoCode"
                      value={promoCodeInput}
                      onChange={onPromoInputChange}
                      onApply={applyPromoCode}
                      applying={promoApplying}
                      valid={promoCodeValid}
                      message={promoCodeMessage}
                    />
                  </FormGroup>

                  <SectionTitle>{t('register.orderSummary', 'Order summary')}</SectionTitle>
                  <CheckoutSummary>
                    {playerCartTotals.lines.map((line) => (
                      <CheckoutLine key={line.variantId}>
                        <span>
                          {line.label}
                          {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                        </span>
                        <span>
                          {(registrationCurrency || 'CHF')} {line.lineTotal.toFixed(2)}
                        </span>
                      </CheckoutLine>
                    ))}
                    {playerCartTotals.deliveryFee > 0 && (
                      <CheckoutLine>
                        <span>{t('register.deliveryLineLabel', 'Shipping')}</span>
                        <span>
                          {(registrationCurrency || 'CHF')}{' '}
                          {playerCartTotals.deliveryFee.toFixed(2)}
                        </span>
                      </CheckoutLine>
                    )}
                    {promoCodeValid && checkoutPromoDiscount > 0 && (
                      <CheckoutLine>
                        <span>{t('register.promoDiscount', 'Promo discount')}</span>
                        <span>
                          − {(registrationCurrency || 'CHF')}{' '}
                          {checkoutPromoDiscount.toFixed(2)}
                        </span>
                      </CheckoutLine>
                    )}
                    <CheckoutTotal>
                      <span>{t('register.total', 'Total')}</span>
                      <span>
                        {(registrationCurrency || 'CHF')}{' '}
                        {Math.max(0, playerCartTotals.payable - checkoutPromoDiscount).toFixed(2)}
                      </span>
                    </CheckoutTotal>
                  </CheckoutSummary>

                  <WizardNavRow>
                    <WizardNavButton type="button" onClick={goWizardBack}>
                      {t('register.backToPackages', 'Back to Packages')}
                    </WizardNavButton>
                    <SubmitButton
                      type="submit"
                      disabled={
                        loading ||
                        (playerCartTotals.hasMerchandise &&
                          partnerUpsell.fulfillmentReady === false)
                      }
                    >
                      {loading
                        ? t('register.processing')
                        : t('register.payNow', 'Pay & register')}
                    </SubmitButton>
                  </WizardNavRow>
                </FormSection>
              )}
                </>
              )}
            </PlayerRegisterShell>
          ) : (
            <>
              <FormSection>
                <SectionTitle>{t('register.teamInformation')}</SectionTitle>
                <FormGroup>
                  <Label htmlFor="teamName">{t('register.teamName')} *</Label>
                  <Input
                    id="teamName"
                    type="text"
                    {...register('teamName', {
                      required: t('register.teamNameRequired'),
                      minLength: { value: 3, message: t('register.teamNameMinLength') },
                    })}
                    className={
                      errors.teamName || errorField === 'teamName' || fieldErrors.teamName
                        ? 'error'
                        : ''
                    }
                    onChange={(e) => {
                      clearFieldError('teamName');
                      register('teamName').onChange(e);
                    }}
                  />
                  {errors.teamName && <ErrorMessage>{errors.teamName.message}</ErrorMessage>}
                  {fieldErrors.teamName && <ErrorMessage>{fieldErrors.teamName}</ErrorMessage>}
                  <HelpText>{t('register.teamNameHelp')}</HelpText>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="canton">{t('register.canton')} *</Label>
                  <Select
                    id="canton"
                    {...register('canton', { required: t('register.cantonRequired') })}
                    className={errors.canton ? 'error' : ''}
                  >
                    <option value="">{t('register.selectCanton')}</option>
                    {(registrationCantons.length > 0
                      ? registrationCantons
                      : getConsolidatedCantonsFilteredByRegions([])
                    ).map((canton) => (
                      <option key={canton.code} value={canton.code}>
                        {canton.name}
                      </option>
                    ))}
                  </Select>
                  {errors.canton && <ErrorMessage>{errors.canton.message}</ErrorMessage>}
                </FormGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>{t('register.promotionalCode')}</SectionTitle>
                <FormGroup>
                  <Label htmlFor="promoCodeLegacy">{t('register.promotionalCode')}</Label>
                  <RegistrationPromoCodeField
                    id="promoCodeLegacy"
                    value={promoCodeInput}
                    onChange={onPromoInputChange}
                    onApply={applyPromoCode}
                    applying={promoApplying}
                    valid={promoCodeValid}
                    message={promoCodeMessage}
                  />
                </FormGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>{t('register.termsAndPayment')}</SectionTitle>
                <PricingSection>
                  <PriceDisplay>
                    {(() => {
                      const cur = registrationCurrency || 'CHF';
                      const baseFromApi =
                        promoCodeValid &&
                        promoCodeData &&
                        typeof promoCodeData.basePrice === 'number' &&
                        !Number.isNaN(promoCodeData.basePrice)
                          ? promoCodeData.basePrice
                          : null;
                      const fullFee =
                        baseFromApi != null ? Number(baseFromApi) : Number(currentPrice);
                      if (fullFee == null || Number.isNaN(fullFee)) {
                        return <CurrentPrice>—</CurrentPrice>;
                      }
                      let fullAfterPromo = fullFee;
                      if (promoCodeValid && promoCodeData) {
                        fullAfterPromo = Math.max(
                          0,
                          fullFee - (Number(promoCodeData.discountAmount) || 0)
                        );
                      }
                      if (promoCodeValid && promoCodeData && fullAfterPromo !== fullFee) {
                        return (
                          <>
                            <OriginalPrice>
                              {cur} {fullFee}
                            </OriginalPrice>
                            <CurrentPrice>
                              {cur} {fullAfterPromo}
                            </CurrentPrice>
                            <DiscountInfo>{promoCodeMessage}</DiscountInfo>
                          </>
                        );
                      }
                      return (
                        <CurrentPrice>
                          {cur} {fullAfterPromo}
                        </CurrentPrice>
                      );
                    })()}
                  </PriceDisplay>
                  <PriceDescription>
                    {seasonStatus?.registrationPage?.priceDescription ||
                      t('register.priceDescription')}
                  </PriceDescription>
                  {currentTeamCount > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                      {currentTeamCount} team{currentTeamCount !== 1 ? 's' : ''} already registered
                    </div>
                  )}
                </PricingSection>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? t('register.processing') : t('register.registerTeam')}
                </SubmitButton>
              </FormSection>
            </>
          )}
        </form>
        )}
      </FormContainer>

      {loading && (
        <LoadingOverlay>
          <LoadingSpinner size="lg" />
          <LoadingText>{t('register.waitingForPayment')}</LoadingText>
        </LoadingOverlay>
      )}

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalIcon>📋</ModalIcon>
              <ModalTitle>{t('register.beforeRegistering')}</ModalTitle>
              <ModalText>
                {t('register.beforeRegisteringText')}
              </ModalText>
            </ModalHeader>
            <ModalButtons>
              <ModalButton 
                $primary 
                onClick={() => setShowModal(false)}
                style={{ 
                  backgroundColor: '#d52b1e', 
                  color: 'white',
                  border: 'none',
                  minWidth: '120px'
                }}
              >
                {t('register.continueRegistration')}
              </ModalButton>
              <ModalLink 
                to="/how-league-works"
                style={{ 
                  backgroundColor: '#043663', 
                  color: 'white',
                  border: 'none',
                  minWidth: '120px'
                }}
              >
                {t('register.viewHowLeagueWorks')}
              </ModalLink>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </RegisterContainer>
  );
};

// Additional styled components for registration status
const RegistrationStatusBanner = styled.div`
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'open') return '#d1fae5';
    if (props.status === 'closed') return '#fee2e2';
    if (props.status === 'upcoming') return '#fef3c7';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.status === 'open') return '#065f46';
    if (props.status === 'closed') return '#991b1b';
    if (props.status === 'upcoming') return '#92400e';
    return '#374151';
  }};
  border: 1px solid ${props => {
    if (props.status === 'open') return '#a7f3d0';
    if (props.status === 'closed') return '#fecaca';
    if (props.status === 'upcoming') return '#fde68a';
    return '#e5e7eb';
  }};
`;

const DisabledForm = styled.div`
  opacity: 0.6;
  pointer-events: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 1;
  }
`;

const RegisterPage = () => {
  return <RegisterForm />;
};

export default RegisterPage;
